import { moneyString, validateLedger } from './financeLedger';

const copy = value => JSON.parse(JSON.stringify(value));
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// Always prepare from the original CSV preview so changing a mapping is reversible.
export function prepareImportAccounts(preview, choices = {}, currentLedger) {
  if (!preview || preview.kind === 'backup' || !preview.data || preview.errors?.length) return preview;
  const errors = [];
  if (!currentLedger || validateLedger(currentLedger).length) {
    return { ...preview, data: null, errors: ['目前帳本格式有誤，請重新讀取後再匯入。'] };
  }
  if (!Array.isArray(preview.accounts) || !Array.isArray(preview.transactions)) {
    return { ...preview, data: null, errors: ['匯入預覽不完整，請重新選擇檔案。'] };
  }
  const currentAccounts = new Map(currentLedger.accounts.map(account => [account.id, account]));
  const newAccounts = new Map(preview.accounts.map(account => [account.id, account]));
  const mappings = new Map();
  const preparedAccounts = [];

  for (const account of preview.accounts) {
    if (currentAccounts.has(account.id)) {
      errors.push(`「${account.name}」的匯入預覽已過期，請重新選擇檔案。`);
      continue;
    }
    const choice = own(choices || {}, account.id) ? choices[account.id] : {};
    if (!choice || typeof choice !== 'object' || Array.isArray(choice)) {
      errors.push(`請確認「${account.name}」的帳戶設定。`);
      continue;
    }
    const targetId = choice.targetId === undefined ? 'new' : choice.targetId;
    if (targetId !== 'new') {
      const target = currentAccounts.get(targetId);
      if (!target || newAccounts.has(targetId)) {
        errors.push(`「${account.name}」只能對應到帳本內既有的帳戶。`);
      } else if (target.currency !== account.currency) {
        errors.push(`「${account.name}」只能對應到相同幣別 ${account.currency} 的帳戶。`);
      } else {
        mappings.set(account.id, targetId);
      }
      continue;
    }
    const opening = moneyString(choice.opening_balance === undefined ? account.opening_balance : choice.opening_balance);
    if (opening === '') errors.push(`「${account.name}」的期初餘額無效，請填最多兩位小數的金額。`);
    const name = choice.name === undefined ? account.name : choice.name;
    const prepared = {
      ...copy(account),
      name: choice.name === undefined ? name : (typeof name === 'string' ? name.trim() : name),
      type: choice.type === undefined ? account.type : choice.type,
      opening_balance: opening,
    };
    preparedAccounts.push(prepared);
    mappings.set(account.id, account.id);
  }

  const transactions = preview.transactions.map(transaction => {
    const entry = { ...copy(transaction), account_id: mappings.get(transaction.account_id) || transaction.account_id };
    if (entry.type === 'transfer') {
      entry.to_account_id = mappings.get(transaction.to_account_id) || transaction.to_account_id;
      if (entry.account_id === entry.to_account_id) {
        errors.push(`轉帳「${entry.name || '未命名轉帳'}」的轉出與轉入帳戶不能合併成同一個帳戶。`);
      }
    }
    return entry;
  });

  const currentBatchIds = new Set(currentLedger.import_batches.map(batch => batch.id));
  if (preview.fingerprint && currentBatchIds.has(preview.fingerprint)) {
    errors.push('這份 CSV 已加入目前帳本，請重新選擇檔案確認，避免重複記帳。');
  }

  // Rebuild from the current ledger: no existing account or transaction is edited
  // by the imported file's names, opening balances, types, or mapping choices.
  const data = {
    ...copy(currentLedger),
    accounts: [...copy(currentLedger.accounts), ...preparedAccounts],
    transactions: [...copy(currentLedger.transactions), ...transactions],
    import_batches: [
      ...copy(currentLedger.import_batches),
      ...copy(preview.data.import_batches.filter(batch => !currentBatchIds.has(batch.id))),
    ],
  };
  errors.push(...validateLedger(data));
  const uniqueErrors = [...new Set(errors)];
  const warnings = (preview.warnings || []).filter(warning => !/^新增 \d+ 個帳戶，期初餘額/.test(warning));
  if (preview.accounts.length && !uniqueErrors.length) {
    const mergedCount = preview.accounts.length - preparedAccounts.length;
    warnings.unshift(preparedAccounts.length
      ? `本次將建立 ${preparedAccounts.length} 個新帳戶${mergedCount ? `，另有 ${mergedCount} 個匯入帳戶連到既有帳戶` : ''}。套用前請核對新帳戶的類型與期初餘額；既有帳戶的設定會保留。`
      : `本次 ${mergedCount} 個匯入帳戶均連到既有帳戶，沿用其帳戶類型與期初餘額。`);
  }
  return {
    ...preview,
    data: uniqueErrors.length ? null : data,
    accounts: preparedAccounts,
    transactions,
    errors: uniqueErrors,
    warnings,
  };
}
